import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './SuperAdminDashboard.css';

const API_BASE_URL = import.meta.env.VITE_API_URL || (
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:3000'
    : 'https://grossistify-api.vercel.app'
);

interface Grossist {
  id: string;
  name: string;
  slug: string;
  email: string;
  phone?: string;
  orgNumber?: string;
  address?: string;
  contactPerson?: string;
  numberOfTrucks: number;
  numberOfDrivers: number;
  domain: string;
  status: string;
  createdAt: string;
  adminCount?: number;
  driverCount?: number;
  activeDrivers?: number;
}

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  grossistId?: string;
  grossistName?: string;
  phone?: string;
  isActive: boolean;
  isOnline: boolean;
  createdAt: string;
}

interface OnboardingLink {
  id: string;
  token: string;
  email?: string;
  companyName?: string;
  status: string;
  createdAt: string;
  expiresAt: string;
  completedAt?: string;
  formData?: {
    companyName: string;
    orgNumber: string;
    contactPerson: string;
    email: string;
    phone: string;
    address?: string;
    numberOfTrucks: number;
    numberOfDrivers: number;
    drivers?: { name: string; phone?: string }[];
    domain?: string;
    submittedAt: string;
  };
}

interface Stats {
  totalGrossists: number;
  activeGrossists: number;
  totalAdmins: number;
  totalDrivers: number;
  activeDrivers: number;
  onlineDrivers: number;
  pendingOnboardings: number;
  totalOnboardings: number;
}

type Tab = 'overview' | 'grossists' | 'users' | 'onboarding';

const SuperAdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [stats, setStats] = useState<Stats | null>(null);
  const [grossists, setGrossists] = useState<Grossist[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [onboardingLinks, setOnboardingLinks] = useState<OnboardingLink[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showCreateGrossist, setShowCreateGrossist] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showCreateLink, setShowCreateLink] = useState(false);
  const [showOnboardingDetail, setShowOnboardingDetail] = useState<OnboardingLink | null>(null);
  const [showCredentials, setShowCredentials] = useState<{ email: string; password: string } | null>(null);
  const [showDriverCredentials, setShowDriverCredentials] = useState<{ name: string; email: string; password: string }[] | null>(null);
  
  // Form states
  const [newGrossist, setNewGrossist] = useState({
    name: '',
    email: '',
    phone: '',
    orgNumber: '',
    contactPerson: '',
    numberOfTrucks: 0,
    numberOfDrivers: 0,
    domain: '',
  });
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'driver',
    grossistId: '',
  });
  
  const [newLink, setNewLink] = useState({
    email: '',
    companyName: '',
  });

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/superadmin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  const fetchGrossists = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/superadmin/grossists`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setGrossists(data.grossists);
      }
    } catch (error) {
      console.error('Error fetching grossists:', error);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/superadmin/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  const fetchOnboardingLinks = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/superadmin/onboarding/links`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setOnboardingLinks(data.links);
      }
    } catch (error) {
      console.error('Error fetching onboarding links:', error);
    }
  }, [token]);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(),
        fetchGrossists(),
        fetchUsers(),
        fetchOnboardingLinks(),
      ]);
      setLoading(false);
    };
    fetchAll();
  }, [fetchStats, fetchGrossists, fetchUsers, fetchOnboardingLinks]);

  const handleCreateGrossist = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/superadmin/grossists`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newGrossist),
      });
      
      if (response.ok) {
        const data = await response.json();
        setShowCreateGrossist(false);
        setShowCredentials(data.adminCredentials);
        setNewGrossist({ name: '', email: '', phone: '', orgNumber: '', contactPerson: '', numberOfTrucks: 0, numberOfDrivers: 0, domain: '' });
        fetchGrossists();
        fetchStats();
      }
    } catch (error) {
      console.error('Error creating grossist:', error);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/superadmin/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newUser),
      });
      
      if (response.ok) {
        const data = await response.json();
        setShowCreateUser(false);
        setShowCredentials(data.credentials);
        setNewUser({ name: '', email: '', phone: '', role: 'driver', grossistId: '' });
        fetchUsers();
        fetchStats();
      }
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  const handleCreateLink = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/superadmin/onboarding/create-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newLink),
      });
      
      if (response.ok) {
        const data = await response.json();
        setShowCreateLink(false);
        setNewLink({ email: '', companyName: '' });
        
        // Show the link
        const url = data.onboardingUrl;
        navigator.clipboard.writeText(url);
        alert(`Onboarding-länk skapad och kopierad!\n\n${url}`);
        
        fetchOnboardingLinks();
      }
    } catch (error) {
      console.error('Error creating link:', error);
    }
  };

  const handleApproveOnboarding = async (link: OnboardingLink) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/superadmin/onboarding/${link.id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      
      if (response.ok) {
        const data = await response.json();
        setShowOnboardingDetail(null);
        setShowCredentials(data.adminCredentials);
        setShowDriverCredentials(data.driverCredentials);
        fetchOnboardingLinks();
        fetchGrossists();
        fetchStats();
      }
    } catch (error) {
      console.error('Error approving onboarding:', error);
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!confirm('Vill du återställa lösenordet för denna användare?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/superadmin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setShowCredentials(data.credentials);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Är du säker på att du vill ta bort denna användare?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/superadmin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        fetchUsers();
        fetchStats();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const pendingOnboardings = onboardingLinks.filter(l => l.status === 'submitted').length;

  return (
    <div className="super-admin">
      {/* Header */}
      <header className="sa-header">
        <div className="sa-header-left">
          <div className="sa-logo">
            <div className="sa-logo-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <span>Grossistify</span>
          </div>
          <span className="sa-badge">Super Admin</span>
        </div>
        
        <div className="sa-header-right">
          <div className="sa-user-info">
            <div className="sa-user-avatar">{user?.name?.charAt(0) || 'S'}</div>
            <span className="sa-user-name">{user?.name}</span>
          </div>
          <button className="sa-logout-btn" onClick={logout}>
            Logga ut
          </button>
        </div>
      </header>

      <div className="sa-content">
        {/* Navigation */}
        <nav className="sa-nav">
          <button
            className={`sa-nav-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7"/>
              <rect x="14" y="3" width="7" height="7"/>
              <rect x="14" y="14" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/>
            </svg>
            Översikt
          </button>
          <button
            className={`sa-nav-btn ${activeTab === 'grossists' ? 'active' : ''}`}
            onClick={() => setActiveTab('grossists')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            Grossister ({grossists.length})
          </button>
          <button
            className={`sa-nav-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            Användare ({users.length})
          </button>
          <button
            className={`sa-nav-btn ${activeTab === 'onboarding' ? 'active' : ''}`}
            onClick={() => setActiveTab('onboarding')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="8.5" cy="7" r="4"/>
              <line x1="20" y1="8" x2="20" y2="14"/>
              <line x1="23" y1="11" x2="17" y2="11"/>
            </svg>
            Onboarding
            {pendingOnboardings > 0 && <span className="badge">{pendingOnboardings}</span>}
          </button>
        </nav>

        {loading ? (
          <div className="sa-loading">
            <div className="sa-spinner" />
            <span>Laddar...</span>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <>
                <div className="sa-stats">
                  <div className="sa-stat-card">
                    <div className="sa-stat-header">
                      <div className="sa-stat-icon purple">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                        </svg>
                      </div>
                    </div>
                    <div className="sa-stat-value">{stats?.totalGrossists || 0}</div>
                    <div className="sa-stat-label">Totalt grossister</div>
                  </div>
                  
                  <div className="sa-stat-card">
                    <div className="sa-stat-header">
                      <div className="sa-stat-icon green">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                        </svg>
                      </div>
                    </div>
                    <div className="sa-stat-value">{stats?.totalDrivers || 0}</div>
                    <div className="sa-stat-label">Totalt förare</div>
                  </div>
                  
                  <div className="sa-stat-card">
                    <div className="sa-stat-header">
                      <div className="sa-stat-icon blue">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                      </div>
                    </div>
                    <div className="sa-stat-value">{stats?.activeDrivers || 0}</div>
                    <div className="sa-stat-label">Aktiva förare</div>
                  </div>
                  
                  <div className="sa-stat-card">
                    <div className="sa-stat-header">
                      <div className="sa-stat-icon orange">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                          <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                      </div>
                    </div>
                    <div className="sa-stat-value">{stats?.pendingOnboardings || 0}</div>
                    <div className="sa-stat-label">Väntande onboardings</div>
                  </div>
                </div>

                {/* Recent Grossists */}
                <div className="sa-section">
                  <div className="sa-section-header">
                    <h3 className="sa-section-title">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                      </svg>
                      Senaste grossisterna
                    </h3>
                    <button className="sa-btn sa-btn-primary" onClick={() => setShowCreateGrossist(true)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                      Ny grossist
                    </button>
                  </div>
                  <div className="sa-table-wrapper">
                    <table className="sa-table">
                      <thead>
                        <tr>
                          <th>Grossist</th>
                          <th>Kontakt</th>
                          <th>Förare</th>
                          <th>Status</th>
                          <th>Skapad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grossists.slice(0, 5).map(g => (
                          <tr key={g.id}>
                            <td>
                              <div className="sa-table-name">{g.name}</div>
                              <div className="sa-table-sub">{g.domain}</div>
                            </td>
                            <td>
                              <div>{g.contactPerson || '-'}</div>
                              <div className="sa-table-sub">{g.email}</div>
                            </td>
                            <td>
                              {g.driverCount || 0} förare
                              <div className="sa-table-sub">{g.activeDrivers || 0} aktiva</div>
                            </td>
                            <td>
                              <span className={`sa-status sa-status-${g.status}`}>
                                <span className="sa-status-dot" />
                                {g.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                              </span>
                            </td>
                            <td>{formatDate(g.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* Grossists Tab */}
            {activeTab === 'grossists' && (
              <div className="sa-section">
                <div className="sa-section-header">
                  <h3 className="sa-section-title">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    </svg>
                    Alla grossister
                  </h3>
                  <button className="sa-btn sa-btn-primary" onClick={() => setShowCreateGrossist(true)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Ny grossist
                  </button>
                </div>
                <div className="sa-table-wrapper">
                  <table className="sa-table">
                    <thead>
                      <tr>
                        <th>Grossist</th>
                        <th>Kontakt</th>
                        <th>Org.nr</th>
                        <th>Förare</th>
                        <th>Status</th>
                        <th>Skapad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grossists.map(g => (
                        <tr key={g.id}>
                          <td>
                            <div className="sa-table-name">{g.name}</div>
                            <div className="sa-table-sub">{g.domain}</div>
                          </td>
                          <td>
                            <div>{g.contactPerson || '-'}</div>
                            <div className="sa-table-sub">{g.email}</div>
                          </td>
                          <td>{g.orgNumber || '-'}</td>
                          <td>
                            {g.driverCount || 0} / {g.numberOfDrivers}
                            <div className="sa-table-sub">{g.activeDrivers || 0} aktiva</div>
                          </td>
                          <td>
                            <span className={`sa-status sa-status-${g.status}`}>
                              <span className="sa-status-dot" />
                              {g.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                            </span>
                          </td>
                          <td>{formatDate(g.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="sa-section">
                <div className="sa-section-header">
                  <h3 className="sa-section-title">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                    </svg>
                    Alla användare
                  </h3>
                  <button className="sa-btn sa-btn-primary" onClick={() => setShowCreateUser(true)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Ny användare
                  </button>
                </div>
                <div className="sa-table-wrapper">
                  <table className="sa-table">
                    <thead>
                      <tr>
                        <th>Användare</th>
                        <th>Roll</th>
                        <th>Grossist</th>
                        <th>Status</th>
                        <th>Skapad</th>
                        <th>Åtgärder</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.id}>
                          <td>
                            <div className="sa-table-name">{u.name}</div>
                            <div className="sa-table-sub">{u.email}</div>
                          </td>
                          <td>
                            <span className={`sa-status ${u.role === 'superadmin' ? 'sa-status-completed' : u.role === 'admin' ? 'sa-status-active' : 'sa-status-pending'}`}>
                              {u.role === 'superadmin' ? 'Super Admin' : u.role === 'admin' ? 'Admin' : 'Förare'}
                            </span>
                          </td>
                          <td>{u.grossistName || '-'}</td>
                          <td>
                            {u.isOnline ? (
                              <span className="sa-status sa-status-active">
                                <span className="sa-status-dot" />
                                Online
                              </span>
                            ) : u.isActive ? (
                              <span className="sa-status sa-status-pending">
                                <span className="sa-status-dot" />
                                Aktiv
                              </span>
                            ) : (
                              <span className="sa-status sa-status-inactive">
                                <span className="sa-status-dot" />
                                Offline
                              </span>
                            )}
                          </td>
                          <td>{formatDate(u.createdAt)}</td>
                          <td>
                            <div className="sa-section-actions">
                              <button
                                className="sa-btn sa-btn-sm sa-btn-secondary"
                                onClick={() => handleResetPassword(u.id)}
                                title="Återställ lösenord"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                </svg>
                              </button>
                              {u.role !== 'superadmin' && (
                                <button
                                  className="sa-btn sa-btn-sm sa-btn-danger"
                                  onClick={() => handleDeleteUser(u.id)}
                                  title="Ta bort"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="3 6 5 6 21 6"/>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Onboarding Tab */}
            {activeTab === 'onboarding' && (
              <div className="sa-section">
                <div className="sa-section-header">
                  <h3 className="sa-section-title">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="8.5" cy="7" r="4"/>
                      <line x1="20" y1="8" x2="20" y2="14"/>
                      <line x1="23" y1="11" x2="17" y2="11"/>
                    </svg>
                    Onboarding-länkar
                  </h3>
                  <button className="sa-btn sa-btn-primary" onClick={() => setShowCreateLink(true)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Skapa länk
                  </button>
                </div>
                {onboardingLinks.length === 0 ? (
                  <div className="sa-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                    </svg>
                    <h3>Inga onboarding-länkar ännu</h3>
                    <p>Skapa en länk för att bjuda in nya grossister</p>
                  </div>
                ) : (
                  <div className="sa-table-wrapper">
                    <table className="sa-table">
                      <thead>
                        <tr>
                          <th>Företag / E-post</th>
                          <th>Status</th>
                          <th>Skapad</th>
                          <th>Utgår</th>
                          <th>Åtgärder</th>
                        </tr>
                      </thead>
                      <tbody>
                        {onboardingLinks.map(link => (
                          <tr key={link.id}>
                            <td>
                              <div className="sa-table-name">{link.formData?.companyName || link.companyName || 'Ej angett'}</div>
                              <div className="sa-table-sub">{link.formData?.email || link.email || '-'}</div>
                            </td>
                            <td>
                              <span className={`sa-status sa-status-${link.status}`}>
                                <span className="sa-status-dot" />
                                {link.status === 'pending' ? 'Väntande' : 
                                 link.status === 'submitted' ? 'Inskickad' : 
                                 link.status === 'completed' ? 'Godkänd' : link.status}
                              </span>
                            </td>
                            <td>{formatDate(link.createdAt)}</td>
                            <td>{formatDate(link.expiresAt)}</td>
                            <td>
                              <div className="sa-section-actions">
                                {link.status === 'submitted' && (
                                  <>
                                    <button
                                      className="sa-btn sa-btn-sm sa-btn-secondary"
                                      onClick={() => setShowOnboardingDetail(link)}
                                    >
                                      Granska
                                    </button>
                                    <button
                                      className="sa-btn sa-btn-sm sa-btn-success"
                                      onClick={() => handleApproveOnboarding(link)}
                                    >
                                      Godkänn
                                    </button>
                                  </>
                                )}
                                {link.status === 'pending' && (
                                  <button
                                    className="sa-btn sa-btn-sm sa-btn-secondary"
                                    onClick={() => {
                                      const url = `${window.location.origin}/onboarding/${link.id}?token=${link.token}`;
                                      copyToClipboard(url);
                                      alert('Länk kopierad!');
                                    }}
                                  >
                                    Kopiera länk
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Grossist Modal */}
      {showCreateGrossist && (
        <div className="sa-modal-overlay" onClick={() => setShowCreateGrossist(false)}>
          <div className="sa-modal" onClick={e => e.stopPropagation()}>
            <div className="sa-modal-header">
              <h2 className="sa-modal-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                </svg>
                Ny grossist
              </h2>
              <button className="sa-modal-close" onClick={() => setShowCreateGrossist(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateGrossist}>
              <div className="sa-modal-body">
                <div className="sa-form-group">
                  <label className="sa-form-label">Företagsnamn *</label>
                  <input
                    type="text"
                    className="sa-form-input"
                    value={newGrossist.name}
                    onChange={e => setNewGrossist({ ...newGrossist, name: e.target.value })}
                    required
                  />
                </div>
                
                <div className="sa-form-row">
                  <div className="sa-form-group">
                    <label className="sa-form-label">E-post *</label>
                    <input
                      type="email"
                      className="sa-form-input"
                      value={newGrossist.email}
                      onChange={e => setNewGrossist({ ...newGrossist, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="sa-form-group">
                    <label className="sa-form-label">Telefon</label>
                    <input
                      type="tel"
                      className="sa-form-input"
                      value={newGrossist.phone}
                      onChange={e => setNewGrossist({ ...newGrossist, phone: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="sa-form-row">
                  <div className="sa-form-group">
                    <label className="sa-form-label">Org.nummer</label>
                    <input
                      type="text"
                      className="sa-form-input"
                      value={newGrossist.orgNumber}
                      onChange={e => setNewGrossist({ ...newGrossist, orgNumber: e.target.value })}
                      placeholder="XXXXXX-XXXX"
                    />
                  </div>
                  <div className="sa-form-group">
                    <label className="sa-form-label">Kontaktperson</label>
                    <input
                      type="text"
                      className="sa-form-input"
                      value={newGrossist.contactPerson}
                      onChange={e => setNewGrossist({ ...newGrossist, contactPerson: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="sa-form-row">
                  <div className="sa-form-group">
                    <label className="sa-form-label">Antal lastbilar</label>
                    <input
                      type="number"
                      className="sa-form-input"
                      value={newGrossist.numberOfTrucks}
                      onChange={e => setNewGrossist({ ...newGrossist, numberOfTrucks: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  <div className="sa-form-group">
                    <label className="sa-form-label">Antal förare</label>
                    <input
                      type="number"
                      className="sa-form-input"
                      value={newGrossist.numberOfDrivers}
                      onChange={e => setNewGrossist({ ...newGrossist, numberOfDrivers: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                </div>
                
                <div className="sa-form-group">
                  <label className="sa-form-label">Domän</label>
                  <input
                    type="text"
                    className="sa-form-input"
                    value={newGrossist.domain}
                    onChange={e => setNewGrossist({ ...newGrossist, domain: e.target.value })}
                    placeholder="exempel.se"
                  />
                  <span className="sa-form-hint">Admin-konto skapas som admin@domän</span>
                </div>
              </div>
              <div className="sa-modal-footer">
                <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setShowCreateGrossist(false)}>
                  Avbryt
                </button>
                <button type="submit" className="sa-btn sa-btn-primary">
                  Skapa grossist
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <div className="sa-modal-overlay" onClick={() => setShowCreateUser(false)}>
          <div className="sa-modal" onClick={e => e.stopPropagation()}>
            <div className="sa-modal-header">
              <h2 className="sa-modal-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <line x1="20" y1="8" x2="20" y2="14"/>
                  <line x1="23" y1="11" x2="17" y2="11"/>
                </svg>
                Ny användare
              </h2>
              <button className="sa-modal-close" onClick={() => setShowCreateUser(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="sa-modal-body">
                <div className="sa-form-group">
                  <label className="sa-form-label">Namn *</label>
                  <input
                    type="text"
                    className="sa-form-input"
                    value={newUser.name}
                    onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                    required
                  />
                </div>
                
                <div className="sa-form-group">
                  <label className="sa-form-label">E-post *</label>
                  <input
                    type="email"
                    className="sa-form-input"
                    value={newUser.email}
                    onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                    required
                  />
                </div>
                
                <div className="sa-form-row">
                  <div className="sa-form-group">
                    <label className="sa-form-label">Roll *</label>
                    <select
                      className="sa-form-select"
                      value={newUser.role}
                      onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                    >
                      <option value="driver">Förare</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="sa-form-group">
                    <label className="sa-form-label">Telefon</label>
                    <input
                      type="tel"
                      className="sa-form-input"
                      value={newUser.phone}
                      onChange={e => setNewUser({ ...newUser, phone: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="sa-form-group">
                  <label className="sa-form-label">Grossist *</label>
                  <select
                    className="sa-form-select"
                    value={newUser.grossistId}
                    onChange={e => setNewUser({ ...newUser, grossistId: e.target.value })}
                    required
                  >
                    <option value="">-- Välj grossist --</option>
                    {grossists.map(g => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="sa-modal-footer">
                <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setShowCreateUser(false)}>
                  Avbryt
                </button>
                <button type="submit" className="sa-btn sa-btn-primary">
                  Skapa användare
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Onboarding Link Modal */}
      {showCreateLink && (
        <div className="sa-modal-overlay" onClick={() => setShowCreateLink(false)}>
          <div className="sa-modal" onClick={e => e.stopPropagation()}>
            <div className="sa-modal-header">
              <h2 className="sa-modal-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
                Skapa onboarding-länk
              </h2>
              <button className="sa-modal-close" onClick={() => setShowCreateLink(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateLink}>
              <div className="sa-modal-body">
                <p style={{ color: '#9ca3af', marginBottom: '1.5rem' }}>
                  Skapa en unik länk som du kan skicka till en potentiell kund. 
                  De fyller i sina uppgifter och du kan sedan godkänna och skapa deras konton.
                </p>
                
                <div className="sa-form-group">
                  <label className="sa-form-label">Företagsnamn (valfritt)</label>
                  <input
                    type="text"
                    className="sa-form-input"
                    value={newLink.companyName}
                    onChange={e => setNewLink({ ...newLink, companyName: e.target.value })}
                    placeholder="Pre-fyll företagsnamnet"
                  />
                </div>
                
                <div className="sa-form-group">
                  <label className="sa-form-label">E-post (valfritt)</label>
                  <input
                    type="email"
                    className="sa-form-input"
                    value={newLink.email}
                    onChange={e => setNewLink({ ...newLink, email: e.target.value })}
                    placeholder="Pre-fyll e-postadressen"
                  />
                </div>
              </div>
              <div className="sa-modal-footer">
                <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setShowCreateLink(false)}>
                  Avbryt
                </button>
                <button type="submit" className="sa-btn sa-btn-primary">
                  Skapa länk
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Onboarding Detail Modal */}
      {showOnboardingDetail && (
        <div className="sa-modal-overlay" onClick={() => setShowOnboardingDetail(null)}>
          <div className="sa-modal sa-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="sa-modal-header">
              <h2 className="sa-modal-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
                Granska ansökan
              </h2>
              <button className="sa-modal-close" onClick={() => setShowOnboardingDetail(null)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="sa-modal-body">
              {showOnboardingDetail.formData && (
                <>
                  <div className="sa-onboarding-detail">
                    <h4>Företagsinformation</h4>
                    <div className="sa-onboarding-field">
                      <label>Företagsnamn:</label>
                      <span>{showOnboardingDetail.formData.companyName}</span>
                    </div>
                    <div className="sa-onboarding-field">
                      <label>Org.nummer:</label>
                      <span>{showOnboardingDetail.formData.orgNumber}</span>
                    </div>
                    <div className="sa-onboarding-field">
                      <label>Kontaktperson:</label>
                      <span>{showOnboardingDetail.formData.contactPerson}</span>
                    </div>
                    <div className="sa-onboarding-field">
                      <label>E-post:</label>
                      <span>{showOnboardingDetail.formData.email}</span>
                    </div>
                    <div className="sa-onboarding-field">
                      <label>Telefon:</label>
                      <span>{showOnboardingDetail.formData.phone}</span>
                    </div>
                    {showOnboardingDetail.formData.address && (
                      <div className="sa-onboarding-field">
                        <label>Adress:</label>
                        <span>{showOnboardingDetail.formData.address}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="sa-onboarding-detail">
                    <h4>Resurser</h4>
                    <div className="sa-onboarding-field">
                      <label>Antal lastbilar:</label>
                      <span>{showOnboardingDetail.formData.numberOfTrucks}</span>
                    </div>
                    <div className="sa-onboarding-field">
                      <label>Antal förare:</label>
                      <span>{showOnboardingDetail.formData.numberOfDrivers}</span>
                    </div>
                    {showOnboardingDetail.formData.domain && (
                      <div className="sa-onboarding-field">
                        <label>Önskad domän:</label>
                        <span>{showOnboardingDetail.formData.domain}</span>
                      </div>
                    )}
                  </div>
                  
                  {showOnboardingDetail.formData.drivers && showOnboardingDetail.formData.drivers.length > 0 && (
                    <div className="sa-onboarding-detail">
                      <h4>Förare ({showOnboardingDetail.formData.drivers.length})</h4>
                      {showOnboardingDetail.formData.drivers.map((driver, i) => (
                        <div key={i} className="sa-onboarding-field">
                          <label>Förare {i + 1}:</label>
                          <span>{driver.name} {driver.phone ? `(${driver.phone})` : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="sa-onboarding-detail">
                    <h4>Inskickat</h4>
                    <div className="sa-onboarding-field">
                      <label>Datum:</label>
                      <span>{formatDate(showOnboardingDetail.formData.submittedAt)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="sa-modal-footer">
              <button className="sa-btn sa-btn-secondary" onClick={() => setShowOnboardingDetail(null)}>
                Stäng
              </button>
              <button className="sa-btn sa-btn-success" onClick={() => handleApproveOnboarding(showOnboardingDetail)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Godkänn & skapa konton
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credentials Modal */}
      {showCredentials && (
        <div className="sa-modal-overlay" onClick={() => { setShowCredentials(null); setShowDriverCredentials(null); }}>
          <div className="sa-modal" onClick={e => e.stopPropagation()}>
            <div className="sa-modal-header">
              <h2 className="sa-modal-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Inloggningsuppgifter
              </h2>
              <button className="sa-modal-close" onClick={() => { setShowCredentials(null); setShowDriverCredentials(null); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="sa-modal-body">
              <div className="sa-credentials">
                <div className="sa-credentials-title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  Admin-konto
                </div>
                <div className="sa-credential-row">
                  <span className="sa-credential-label">E-post:</span>
                  <span className="sa-credential-value">{showCredentials.email}</span>
                  <button className="sa-copy-btn" onClick={() => copyToClipboard(showCredentials.email)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                  </button>
                </div>
                <div className="sa-credential-row">
                  <span className="sa-credential-label">Lösenord:</span>
                  <span className="sa-credential-value">{showCredentials.password}</span>
                  <button className="sa-copy-btn" onClick={() => copyToClipboard(showCredentials.password)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                  </button>
                </div>
              </div>
              
              {showDriverCredentials && showDriverCredentials.length > 0 && (
                <div className="sa-credentials" style={{ marginTop: '1rem', background: 'rgba(59, 130, 246, 0.1)', borderColor: 'rgba(59, 130, 246, 0.3)' }}>
                  <div className="sa-credentials-title" style={{ color: '#60a5fa' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    Förarkonton ({showDriverCredentials.length})
                  </div>
                  {showDriverCredentials.map((driver, i) => (
                    <div key={i} style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: i < showDriverCredentials.length - 1 ? '1px solid rgba(59, 130, 246, 0.2)' : 'none' }}>
                      <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#fff' }}>{driver.name}</div>
                      <div className="sa-credential-row">
                        <span className="sa-credential-label">E-post:</span>
                        <span className="sa-credential-value">{driver.email}</span>
                        <button className="sa-copy-btn" onClick={() => copyToClipboard(driver.email)}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                          </svg>
                        </button>
                      </div>
                      <div className="sa-credential-row">
                        <span className="sa-credential-label">Lösenord:</span>
                        <span className="sa-credential-value">{driver.password}</span>
                        <button className="sa-copy-btn" onClick={() => copyToClipboard(driver.password)}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginTop: '1rem' }}>
                ⚠️ Spara dessa uppgifter säkert. Lösenordet visas endast en gång.
              </p>
            </div>
            <div className="sa-modal-footer">
              <button className="sa-btn sa-btn-primary" onClick={() => { setShowCredentials(null); setShowDriverCredentials(null); }}>
                Stäng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
